import { Box } from '@mui/material';
import { __SWEEP_DESIGN_TYPOGRAPHY_STYLES___, colors } from '@sweep-io/sweep-design';
import { Link as LinkIcon } from '@sweep-io/sweep-design/dist/icons';
import {
  ReactNode,
  useEffect,
  useState,
  TableHTMLAttributes,
  AnchorHTMLAttributes,
  forwardRef,
  createContext,
  useContext,
} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css'; // `rehype-katex` does not import the CSS automatically
import { dataTableVariants } from './table/dataTableVariants';
import { DataTableVariant } from './table/TableTypes';
import { preprocessLaTeX } from '../../third-party/preprocessLaTeX';
import { TableCopyButton } from './ai-chat/message-atoms/TableCopyButton';

// Remove surrounding brackets from external links only: "([text](https://...))" -> "[text](https://...)"
const removeBracketsAroundLinks = (content: string): string => {
  // Match parentheses around links with external URLs (http:// or https://)
  return content.replace(/\(\[([^\]]+)\]\((https?:\/\/[^)]+)\)\)/g, '[$1]($2)');
};

/**
 * TABLE REQUIREMENTS CHALLENGE:
 * 1. Text selection must work (broken by react-markdown's components prop)
 * 2. Horizontal scrolling for wide tables (requires wrapper div)
 * 3. Copy button on hover (requires DOM access + positioning)
 * 4. Dynamic copy button visibility (requires prop passing to stable components)
 *
 * SOLUTION ARCHITECTURE:
 * - Context: Pass dynamic props to stable components (fixes text selection)
 * - ForwardRef: Stable component reference (fixes react-markdown interference)
 * - State + Callback Ref: DOM access + React reactivity (fixes copy button)
 * - Wrapper div: Horizontal scrolling (fixes table layout)
 */

// Context to pass dynamic props to stable component (avoids inline component creation)
const TableConfigContext = createContext({ shouldShowCopyButton: false });

// Custom link component - renders external links as pills, passes through for others
const CustomLink = forwardRef<
  HTMLAnchorElement,
  AnchorHTMLAttributes<HTMLAnchorElement> & { children?: ReactNode }
>(({ children, href, ...props }, ref) => {
  const isExternalLink = href?.startsWith('http://') || href?.startsWith('https://');

  if (isExternalLink) {
    return (
      <Box
        component="a"
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
        sx={{
          '&&': {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 12px',
            backgroundColor: colors.blue[100],
            color: colors.blue[500],
            borderRadius: '16px',
            textDecoration: 'none',
            fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['caption']?.fontSize,
            lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['caption']?.lineHeight,
            fontWeight: 500,
            transition: 'background-color 0.15s ease',
          },
          '&:hover': {
            backgroundColor: colors.blue[300],
          },
        }}
      >
        <LinkIcon size="small" color={colors.blue[500]} />
        {children}
      </Box>
    );
  }

  // Non-external links - render as regular anchor, inherits parent's & a styles
  return (
    <a ref={ref} href={href} {...props}>
      {children}
    </a>
  );
});

// Stable forwardRef component (fixes react-markdown text selection interference)
const CustomTable = forwardRef<
  HTMLTableElement,
  TableHTMLAttributes<HTMLTableElement> & { children?: ReactNode }
>(({ children, ...props }, ref) => {
  const { shouldShowCopyButton } = useContext(TableConfigContext);
  return (
    <TableWrapper ref={ref} shouldShowCopyButton={shouldShowCopyButton} tableProps={props}>
      {children}
    </TableWrapper>
  );
});

const TableWrapper = forwardRef<
  HTMLTableElement,
  {
    children: ReactNode;
    shouldShowCopyButton: boolean;
    tableProps: TableHTMLAttributes<HTMLTableElement>;
  }
>(({ children, shouldShowCopyButton, tableProps }, ref) => {
  // State for DOM element (refs don't trigger re-renders)
  const [tableElement, setTableElement] = useState<HTMLTableElement | null>(null);
  const [buttonPosition, setButtonPosition] = useState({ top: '8px' });

  // Callback ref: combines state tracking + ref forwarding
  const handleRef = (el: HTMLTableElement | null) => {
    setTableElement(el); // For React reactivity
    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      ref.current = el; // For react-markdown compatibility
    }
  };

  // Calculate copy button position from table header height
  useEffect(() => {
    if (shouldShowCopyButton && tableElement) {
      const thead = tableElement.querySelector('thead');
      if (thead) {
        const theadHeight = thead.offsetHeight;
        const buttonTop = Math.max(8, theadHeight / 2 - 12);
        setButtonPosition({ top: `${buttonTop}px` });
      }
    }
  }, [children, shouldShowCopyButton, tableElement]);

  return (
    <Box
      sx={{
        position: 'relative', // For absolute copy button positioning
        overflow: 'hidden',
        ...(shouldShowCopyButton && {
          '&:hover .table-copy-button': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }),
      }}
    >
      {/* Wrapper div: enables horizontal scrolling (table can't have overflow:auto) */}
      <Box
        className="table-scroller"
        sx={{
          overflowX: 'auto',
          borderRadius: '8px',
          border: `1px solid ${colors.grey[120]}`,
          '&::-webkit-scrollbar': {
            width: '4px',
            height: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: colors.grey[120] + '!important',
          },
          ...(shouldShowCopyButton && {
            '& thead th:last-child': {
              paddingRight: '40px',
            },
          }),
        }}
      >
        <table ref={handleRef} {...tableProps}>
          {children}
        </table>
      </Box>

      {/* Copy button: positioned absolutely, rendered conditionally based on state */}
      {shouldShowCopyButton && tableElement && (
        <Box
          className="table-copy-button"
          sx={{
            position: 'absolute',
            top: buttonPosition.top,
            right: '8px',
            zIndex: 10,
            opacity: 0,
            pointerEvents: 'none',
            backgroundColor: colors.grey[100],
            borderRadius: '50%',
          }}
        >
          <TableCopyButton tableElement={tableElement} />
        </Box>
      )}
    </Box>
  );
});

export type MarkdownWrapperProps = {
  content: ReactNode;
  component?: 'div' | 'span';
  color?: string;
  removeMargins: boolean; //margins are not needed in case there's no background color
  dataTableVariant?: DataTableVariant;
  shouldShowTableCopyButtons?: boolean;
};
export const MarkdownWrapper = ({
  content,
  component = 'div',
  color,
  removeMargins,
  dataTableVariant = DataTableVariant.small,
  shouldShowTableCopyButtons = false,
}: MarkdownWrapperProps) => {
  const tableVariantProps = dataTableVariants[dataTableVariant];
  return (
    <Box
      component={component}
      sx={{
        color: color ?? colors.grey[900],
        fontFamily: "'Inter', sans-serif",
        overflow: 'hidden',
        '&>*:first-of-type': {
          '&:not(hr)': {
            marginTop: removeMargins ? 0 : '12px',
          },
        },
        '&>*:last-child': {
          '&:not(hr)': {
            marginBottom: removeMargins ? 0 : '12px',
          },
        },
        '& hr': {
          margin: '48px 0',
          borderColor: colors.grey[120],
          borderStyle: 'solid',
          borderBottom: 'none',
        },
        '& code': {
          wordBreak: 'break-word',
          whiteSpace: 'break-spaces',
          background: colors.grey[120],
        },
        '& p, & a': {
          fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.body?.fontSize,
          lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.body?.lineHeight,
          fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.body?.fontWeight,
          wordBreak: 'break-word',
        },
        '& hr, & pre, & li, & ol, & ul': {
          fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.body?.fontSize,
          wordBreak: 'break-word',
        },
        '& strong': {
          fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['body-bold']?.fontWeight,
        },
        '& h1': {
          fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['h1-medium']?.fontSize,
          lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['h1-medium']?.lineHeight,
          fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['h1-medium']?.fontWeight,
        },
        '& h2': {
          fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['h2-medium']?.fontSize,
          lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['h2-medium']?.lineHeight,
          fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['h2-medium']?.fontWeight,
        },
        '& h3': {
          fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4?.fontSize,
          lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4?.lineHeight,
          fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4?.fontWeight,
        },
        '& h4': {
          fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4?.fontSize,
          lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4?.lineHeight,
          fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4?.fontWeight,
        },
        '& h5': {
          fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4?.fontSize,
          lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4?.lineHeight,
          fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___.h4?.fontWeight,
        },
        '& h6': {
          fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['caption-bold']?.fontSize,
          lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['caption-bold']?.lineHeight,
          fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___['caption-bold']?.fontWeight,
        },
        '& table': {
          borderSpacing: 0,
          width: '100%',
          border: 'none',
          borderRadius: 0,
          fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[tableVariantProps.fontVariant]?.fontSize,

          '& th': {
            backgroundColor: colors.grey[100],
            color: tableVariantProps.headerFontColor,
            fontWeight:
              __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[tableVariantProps.fontVariant]?.fontWeight,
            textAlign: 'left',
            padding: tableVariantProps.cellPadding,
            minWidth: tableVariantProps.minRowWidth,
            wordBreak: 'break-word',
            lineHeight:
              __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[tableVariantProps.fontVariant]?.lineHeight,
          },

          '& tr': {
            '& td': {
              whiteSpace: 'break-spaces',
              wordBreak: 'break-word',
              borderBottom: `1px solid ${colors.grey[120]}`,
              color: tableVariantProps.fontColor,
              fontWeight:
                __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[tableVariantProps.fontVariant]?.fontWeight,
              lineHeight:
                __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[tableVariantProps.fontVariant]?.lineHeight,
              textAlign: 'left',
              padding: tableVariantProps.cellPadding,
            },
            '&:last-child': {
              '& td': {
                borderBottom: 'none',
              },
            },
          },
          '& + ul:first-of-type:not(hr)': {
            //Added because there was no space between a table and a ul. Needed ":first-of-type:not(hr)" for getting css priority.
            marginTop: '8px',
          },
        },
      }}
    >
      {typeof content === 'string' && (
        <TableConfigContext.Provider value={{ shouldShowCopyButton: shouldShowTableCopyButtons }}>
          <Markdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex, rehypeRaw]}
            children={removeBracketsAroundLinks(preprocessLaTeX(content) ?? '')}
            components={{
              table: CustomTable, // Stable reference (critical for text selection)
              a: CustomLink,
            }}
          />
        </TableConfigContext.Provider>
      )}
      {typeof content !== 'string' && <>{content}</>}
    </Box>
  );
};
